<template>
  <TemplateBase :page="page">
    <!-- Slot with mobile lyre link component, color is optional in white, default is black, only visible until xl screens, positioning is inside the component -->
    <template #mobileLyre>
      <MoleculeButtonMobileLyre :flush="false" :white-lyre="false" />
    </template>

    <!-- Slot with back button with flush false so it is positioned to the header -->
    <template #backButton>
      <MoleculeButtonBreadcrumbBack :breadcrumbs="breadcrumbs" />
    </template>

    <!-- This is where the main part of the template starts, the main tag lives in Base.vue, don't put it in other templates -->

    <!-- Start header -->
    <OrganismOverviewHeaderAndFiltersControl
      :active-facets="activeFacets"
      :facets="facets"
      :introduction="$t('overview-series-description', { count: totalHits })"
      :introduction-mobile="$t('overview-series-description-mobile', { count: totalHits })"
      :keyword="keyword"
      :title="page.title"
      :total-hits="totalHits"
      class="mb-4 sm:mb-8 lg:mb-8"
      translation-group="series-overview"
      @reset="reset"
      @set-keyword="setKeyword"
      @toggle-facet="toggleFacet"
    />
    <!-- End header -->

    <!-- Rest of the page -->
    <section class="container mx-auto v-page-section-container-padding lg:grid lg:grid-cols-12 gap-x-8">
      <!-- This div shows when you selected an organizer -->
      <div
        v-if="highlightedOrganizer"
        class="col-span-full xl:col-start-2 xl:col-end-12 2xl:col-start-3 2xl:col-end-11 border-t border-b md:border-t-2 md:border-b-2 border-white border-solid my-4 py-5 sm:mb-8 lg:mb-8"
      >
        <h2 class="font-semibold text-3xl leading-tight text-app-primary-1-red--fire-brick">{{ highlightedOrganizer.title }}</h2>
        <h3 v-if="highlightedOrganizer.subtitle" class="text-lg leading-tight mb-4">{{ highlightedOrganizer.subtitle }}</h3>
        <div class="md:grid grid-cols-2 md:gap-x-8 lg:gap-x-4">
          <MoleculePicture class="block w-full h-full object-cover rounded-3xl overflow-hidden mb-4 md:mb-0" :image="highlightedOrganizer.image" />
          <div v-if="highlightedOrganizer.introduction" class="font-semibold text-sm lg:text-base" v-html="highlightedOrganizer.introduction" />
        </div>
      </div>
      <!-- End selected organizer -->

      <!-- Results -->
      <ul class="col-span-full xl:col-start-2 xl:col-end-12 2xl:col-start-3 2xl:col-end-11">
        <li v-for="(series, index) of seriesList" :key="series.uri" class="mb-6">
          <MoleculeCardEventAndSeries
            v-if="!series.loading"
            :label="$t('overview-series-card-label')"
            :url="series.url"
            :title="series.title"
            :body="series.introduction"
            :location="series.room ? series.room.title : undefined"
            :when-description="series.events[0] ? $t('overview-series-card-when-description', { eventDate: $d(new Date(series.events[0].eventDate), 'long-date') }) : undefined"
            :tix-pricing="{ minPrice: series.minPrice }"
            :tix-sale="prepareTixSale(series)"
            :image="series.teaserImage"
            :discount="series.discount"
            :can-be-favorited="false"
          />
          <div v-if="bannersAtPosition[index]" class="my-8">
            <MoleculeCardBanner v-bind="bannerProps(bannersAtPosition[index])" />
          </div>
        </li>
      </ul>

      <OrganismPagination :count="totalHits" :limit="hitsPerPage" :page="currentPage" class="col-span-full mt-12 mb-16" @click="({ page: newPage }) => setCurrentPage(newPage)" />
    </section>
  </TemplateBase>
</template>

<script setup lang="ts">
defineProps({
  breadcrumbs: { type: Array as PropType<any[]>, required: true },
  limit: { type: Number, required: false, default: 15 },
  page: { type: Object as PropType<Record<string, any>>, required: true },
})

const prepareTixSale = setupPrepareTixSale()

const route = useRoute()
const router = useRouter()
const seriesOverviewStore = useSeriesOverviewStore()

const highlightedOrganizer = computed(() => seriesOverviewStore.highlightedOrganizer)
const activeFacets = computed(() => seriesOverviewStore.activeFacets)
const bannersAtPosition = computed(() => seriesOverviewStore.bannersAtPosition)
const currentPage = computed(() => seriesOverviewStore.page)
const seriesList = computed(() => seriesOverviewStore.series)
const facets = computed(() => seriesOverviewStore.facets)
const hitsPerPage = computed(() => seriesOverviewStore.hitsPerPage)
const keyword = computed(() => seriesOverviewStore.keyword)
const totalHits = computed(() => seriesOverviewStore.totalHits)
const urlParameters = computed(() => seriesOverviewStore.urlParameters)

/*
 * Maps a CMS banner block onto MoleculeCardBanner's props. A plain function rather than the previous
 * `v-if="banner = bannersAtPosition[index]"` assignment, which created the alias by writing to the
 * render context.
 */
const bannerProps = (banner: CmsData) => ({
  title: banner.title,
  topLabel: banner.label,
  subLabel: banner.subtitle,
  icon: banner.icon ? banner.icon.slug : undefined,
  image: banner.image,
  buttonTitle: banner.buttonTitle,
  buttonUrl: banner.buttonUrl,
  theme: banner.theme,
})

function reset() {
  seriesOverviewStore.reset()
}
function setCurrentPage(newPage: number) {
  seriesOverviewStore.setPage(newPage)
}
function setKeyword(newKeyword: string) {
  seriesOverviewStore.setKeyword(newKeyword)
}
function toggleFacet(facet: { name: string; value: string }) {
  seriesOverviewStore.toggleFacet(facet)
}
function setUrlParameters(query: CmsData) {
  seriesOverviewStore.setUrlParameters(query)
}

watch(
  () => route.query,
  (query) => {
    if (!routerQueryIsEqual(urlParameters.value, query)) {
      setUrlParameters(query)
    }
  },
)
watch(urlParameters, (query) => {
  if (!routerQueryIsEqual(route.query, query)) {
    router.push({ query })
  }
})
</script>
